import { useEffect, useState } from 'react';
import api from '@/api';
import { Tag } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PlatformBadge = ({ platform }) => {
  const map = { demaxtore: 'bg-sky-50 text-sky-700', commoditybid: 'bg-orange-50 text-orange-700', freightiq: 'bg-green-50 text-green-700' };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${map[platform] || 'bg-slate-50 text-slate-600'}`}>{platform}</span>;
};

export default function AdminOffers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/admin/offers').then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/offers/${id}/status`, { status });
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-offers-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">All Offers</h1>
        <p className="text-slate-500 text-sm mt-0.5">Offers across all platforms</p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center">
              <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No offers yet</p>
            </div>
          ) : (
            <table className="w-full" data-testid="admin-offers-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Supplier</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platform</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Notes</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map(item => (
                  <tr key={item._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4 text-sm font-semibold text-slate-900">{item.supplier_name}</td>
                    <td className="px-5 py-4"><PlatformBadge platform={item.platform} /></td>
                    <td className="px-5 py-4 text-sm font-bold text-slate-900">${item.amount?.toLocaleString()}</td>
                    <td className="px-5 py-4 hidden lg:table-cell text-sm text-slate-500 line-clamp-1">{item.notes || '—'}</td>
                    <td className="px-5 py-4">
                      <Select value={item.status} onValueChange={v => updateStatus(item._id, v)}>
                        <SelectTrigger className="w-28 h-7 text-xs" data-testid={`offer-status-${item._id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['pending','accepted','rejected'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
